/**
 * Schedules Import Wizard — Excel-safe .xlsx / .csv round-trip for service schedules.
 * Mirrors the Device Import Wizard pattern:
 *   Step 1 — Upload (drag & drop, templates)
 *   Step 2 — Review: rows are split into Odometer / Engine Hours / Date tabs so each tab
 *            only shows the columns that matter. Row selection + Clone-from-existing-schedule
 *            + Create-only / Update-existing toggle.
 *   Step 3 — Dry-run summary
 *   Step 4 — Result (with 30-min undo pointer)
 * Create adds new schedules; Update overwrites matching (vehicle + task) schedules and
 * snapshots their previous state so the whole import can be undone for 30 minutes.
 */
import React, { useState, useMemo, useCallback } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Checkbox } from '../ui/checkbox';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../ui/select';
import { RadioGroup, RadioGroupItem } from '../ui/radio-group';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../ui/tabs';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { Command, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem } from '../ui/command';
import { Loader2, CheckCircle, AlertTriangle, FileSpreadsheet, ArrowRight, ArrowLeft, Download, Copy, ChevronDown } from 'lucide-react';
import { ImportHelpPanel, MaintHelpTip } from '../fleet/MaintenanceHelp';
import { toast } from 'sonner';
import * as XLSX from 'xlsx';
import { getFileTimestamp } from '../../utils/timezone';

const COLUMNS = [
  'Vehicle', 'Device IMEI', 'Task Name', 'Basis', 'Interval (km/hrs)', 'Interval (days)',
  'Last Serviced (km/hrs)', 'Last Serviced Date', 'Recurring', 'Fixed Schedule', 'Notes',
];
const TEXT_FIELDS = new Set(['Vehicle', 'Device IMEI', 'Last Serviced Date']);
const BASES = ['odometer', 'engine_hours', 'calendar', 'odometer_calendar', 'engine_hours_calendar'];
const BASIS_ALIASES = {
  odometer: 'odometer', km: 'odometer', kms: 'odometer', mileage: 'odometer',
  engine_hours: 'engine_hours', hours: 'engine_hours', hrs: 'engine_hours', 'engine hours': 'engine_hours',
  calendar: 'calendar', date: 'calendar', days: 'calendar', time: 'calendar',
  odometer_calendar: 'odometer_calendar', 'odometer+calendar': 'odometer_calendar', 'odometer_date': 'odometer_calendar',
  engine_hours_calendar: 'engine_hours_calendar', 'engine_hours+calendar': 'engine_hours_calendar', 'engine_hours_date': 'engine_hours_calendar',
};
// One example row per basis so the blank template makes the 3 tabs (Odometer / Engine Hours / Date)
// and the dual basis self-explanatory. Dates are ISO here; the template writer reformats them to the
// user's date format. Reminders are NOT in the sheet — they come from Clone (basis-safe, full stages).
const EXAMPLE_ROWS = [
  ['Truck 12', '', 'Oil Change', 'odometer', '10000', '', '10000', '', 'yes', 'yes', 'Odometer example — full synthetic'],
  ['Excavator 3', '', 'Hydraulic Service', 'engine_hours', '250', '', '1200', '', 'yes', 'yes', 'Engine-hours example'],
  ['Van 7', '', 'Rego Renewal', 'calendar', '', '365', '', '2026-01-01', 'yes', 'no', 'Date example — annual registration'],
  ['Bus 9', '', 'Major Service', 'odometer_calendar', '30000', '180', '30000', '2026-01-01', 'yes', 'yes', 'Dual example — whichever comes first'],
];
const DATE_COL = 'Last Serviced Date';

const isMetric = (b) => b === 'odometer' || b === 'engine_hours' || b === 'odometer_calendar' || b === 'engine_hours_calendar';
const hasCalendar = (b) => b === 'calendar' || b === 'odometer_calendar' || b === 'engine_hours_calendar';
const isDual = (b) => b === 'odometer_calendar' || b === 'engine_hours_calendar';
const canonBasis = (raw) => BASIS_ALIASES[String(raw || '').trim().toLowerCase()] || '';
const tabForBasis = (b) => {
  if (b === 'engine_hours' || b === 'engine_hours_calendar') return 'engine_hours';
  if (b === 'calendar') return 'date';
  return 'odometer'; // odometer, odometer_calendar, blank/invalid
};
const basisFamily = (b) => tabForBasis(b); // odometer/engine_hours/date — clone compatibility group
const yesNo = (v, dflt = true) => {
  if (v === '' || v == null) return dflt;
  return /^(y|yes|true|1)$/i.test(String(v).trim());
};

/* ── Date helpers ── export in the user's format; import parses flexibly and normalises to ISO ── */
const toYMD = (iso) => {
  if (!iso) return null;
  const s = String(iso).slice(0, 10);
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  return m ? { y: m[1], mo: m[2], d: m[3] } : null;
};
// Render an ISO/YYYY-MM-DD date into the user's Settings date format (for export & template).
function formatDateOut(iso, dateFormat = 'DD/MM/YYYY') {
  const p = toYMD(iso);
  if (!p) return '';
  if (dateFormat === 'MM/DD/YYYY') return `${p.mo}/${p.d}/${p.y}`;
  if (dateFormat === 'YYYY-MM-DD') return `${p.y}-${p.mo}-${p.d}`;
  return `${p.d}/${p.mo}/${p.y}`;
}
// Parse an imported/typed date of unknown format → canonical 'YYYY-MM-DD' (or '' if unparseable).
// Interprets ambiguous DD/MM vs MM/DD using the user's configured format first.
function parseDateIn(raw, dateFormat = 'DD/MM/YYYY') {
  const s = String(raw || '').trim();
  if (!s) return '';
  if (/^\d{4}-\d{2}-\d{2}/.test(s)) return s.slice(0, 10);           // ISO date or datetime
  const parts = s.split(/[/\-.]/).map((x) => x.trim());
  if (parts.length !== 3 || parts.some((p) => p === '' || !/^\d+$/.test(p))) return '';  // strict: 3 numeric parts only
  let y, mo, d;
  if (parts[0].length === 4) { [y, mo, d] = parts; }                 // YYYY x x
  else if (dateFormat === 'MM/DD/YYYY') { [mo, d, y] = parts; }       // US
  else { [d, mo, y] = parts; }                                       // DD/MM (default & AU)
  y = String(y); mo = String(mo).padStart(2, '0'); d = String(d).padStart(2, '0');
  if (y.length === 2) y = `20${y}`;
  const iso = `${y}-${mo}-${d}`;
  return (/^\d{4}-\d{2}-\d{2}$/.test(iso) && !isNaN(Date.parse(iso))) ? iso : '';
}

const TAB_META = {
  odometer: { label: 'Odometer (km)', unit: 'km', bases: [['odometer', 'Odometer'], ['odometer_calendar', 'Odometer + Date']] },
  engine_hours: { label: 'Engine Hours', unit: 'hrs', bases: [['engine_hours', 'Engine Hours'], ['engine_hours_calendar', 'Engine Hrs + Date']] },
  date: { label: 'Date', unit: 'days', bases: [['calendar', 'Date']] },
};

function buildSheet(rows) {
  const ws = XLSX.utils.aoa_to_sheet(rows);
  const range = XLSX.utils.decode_range(ws['!ref']);
  for (let c = 0; c <= range.e.c; c++) {
    const header = rows[0][c];
    if (!TEXT_FIELDS.has(header)) continue;
    for (let r = 1; r <= range.e.r; r++) {
      const addr = XLSX.utils.encode_cell({ c, r });
      if (ws[addr] && ws[addr].v != null) { ws[addr].t = 's'; ws[addr].v = String(ws[addr].v); ws[addr].z = '@'; }
    }
  }
  ws['!cols'] = COLUMNS.map((h) => ({ wch: Math.max(12, h.length + 2) }));
  return ws;
}

const dateIdx = COLUMNS.indexOf(DATE_COL);
const fmtRowDates = (rows, dateFormat) => rows.map((r) => r.map((c, i) => (i === dateIdx ? formatDateOut(c, dateFormat) : c)));

export function downloadScheduleTemplateXlsx(dateFormat = 'DD/MM/YYYY') {
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, buildSheet([COLUMNS, ...fmtRowDates(EXAMPLE_ROWS, dateFormat)]), 'Schedules');
  XLSX.writeFile(wb, 'service_schedules_template.xlsx');
}
export function downloadScheduleTemplateCsv(dateFormat = 'DD/MM/YYYY') {
  const csv = [COLUMNS, ...fmtRowDates(EXAMPLE_ROWS, dateFormat)].map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(',')).join('\r\n');
  const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' });
  const a = document.createElement('a'); a.href = URL.createObjectURL(blob); a.download = 'service_schedules_template.csv'; a.click();
}

function scheduleToRow(s, devicesById, dateFormat) {
  const dev = devicesById[s.device_id] || {};
  return [
    s.asset_name || s.unit_number || s.device_name || dev.name || '', dev.imei || '', s.task_name || '', s.basis || '',
    isMetric(s.basis) ? (s.interval_value ?? '') : '', hasCalendar(s.basis) ? (s.interval_days ?? '') : '',
    isMetric(s.basis) ? (s.baseline_value ?? '') : '', hasCalendar(s.basis) ? formatDateOut(s.baseline_date, dateFormat) : '',
    s.recurring ? 'yes' : 'no', s.fixed_schedule !== false ? 'yes' : 'no', s.notes || '',
  ];
}
export function exportSchedules(schedules, devicesById, format, dateFormat = 'DD/MM/YYYY') {
  const rows = [COLUMNS, ...schedules.map((s) => scheduleToRow(s, devicesById, dateFormat))];
  const fname = `service_schedules_${getFileTimestamp()}`;
  if (format === 'csv') {
    const csv = rows.map((r) => r.map((c) => `"${String(c ?? '').replace(/"/g, '""')}"`).join(',')).join('\r\n');
    const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' });
    const a = document.createElement('a'); a.href = URL.createObjectURL(blob); a.download = `${fname}.csv`; a.click();
  } else {
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, buildSheet(rows), 'Schedules');
    XLSX.writeFile(wb, `${fname}.xlsx`);
  }
}

const StepDot = ({ n, step, label }) => (
  <div className="flex items-center gap-2">
    <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-semibold ${step === n ? 'bg-[#2563eb] text-white' : step > n ? 'bg-emerald-500 text-white' : 'bg-muted text-muted-foreground'}`}>
      {step > n ? <CheckCircle className="w-3.5 h-3.5" /> : n}
    </div>
    <span className={`text-xs ${step === n ? 'font-semibold' : 'text-muted-foreground'}`}>{label}</span>
  </div>
);

const schedLabel = (s) => s.task_name || 'Untitled task';
const schedVehicle = (s) => s.asset_name || s.unit_number || s.device_name || '—';

/* Clone-from-existing-schedule picker (mirrors the Device wizard ClonePicker) */
const ScheduleClonePicker = ({ value, onChange, options, testid }) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const selected = options.find((o) => o.id === value);
  const filtered = (options || []).filter((s) => {
    if (!query) return true;
    const q = query.toLowerCase();
    return String(schedLabel(s)).toLowerCase().includes(q) || String(schedVehicle(s)).toLowerCase().includes(q);
  }).sort((a, b) => String(schedLabel(a)).localeCompare(String(schedLabel(b)), undefined, { sensitivity: 'base' }));
  return (
    <Popover open={open} onOpenChange={setOpen} modal>
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm" className="h-8 justify-between min-w-[260px]" data-testid={testid}>
          <span className="truncate text-left">{selected ? `${schedLabel(selected)} — ${schedVehicle(selected)}` : 'Pick source schedule…'}</span>
          <ChevronDown className="w-3.5 h-3.5 opacity-60" />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="p-0 w-[340px] z-[10000]">
        <Command>
          <CommandInput placeholder="Search by task or vehicle…" value={query} onValueChange={setQuery} />
          <CommandList>
            <CommandEmpty>No schedules found.</CommandEmpty>
            <CommandGroup>
              {filtered.slice(0, 80).map((s) => (
                <CommandItem key={s.id} onSelect={() => { onChange(s.id); setOpen(false); }} className="cursor-pointer">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm truncate">{schedLabel(s)}</p>
                    <p className="text-[10px] text-muted-foreground truncate">{schedVehicle(s)} · {(s.reminder_stages || []).length} stage(s)</p>
                  </div>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};

const CLONE_FIELDS = [
  ['reminder_stages', 'Reminder stages'],
  ['recurring', 'Recurring'],
  ['fixed_schedule', 'Fixed schedule'],
  ['interval', 'Interval'],
  ['notes', 'Notes'],
];

export default function ScheduleImportWizard({ open, onClose, api, devices = [], existingSchedules = [], onImported, dateFormat = 'DD/MM/YYYY' }) {
  const [step, setStep] = useState(1);
  const [rows, setRows] = useState([]);           // [{ data, selected, cloneOverrides, cloned }]
  const [parsing, setParsing] = useState(false);
  const [applying, setApplying] = useState(false);
  const [results, setResults] = useState(null);
  const [fileName, setFileName] = useState('');
  const [dragOver, setDragOver] = useState(false);
  const [activeTab, setActiveTab] = useState('odometer');
  const [cloneSourceId, setCloneSourceId] = useState('');
  const [cloneFields, setCloneFields] = useState({ reminder_stages: true, recurring: false, fixed_schedule: false, interval: false, notes: false });
  const [duplicateMode, setDuplicateMode] = useState('skip'); // 'skip' | 'update'
  const [fieldMode, setFieldMode] = useState('merge');        // 'merge' | 'replace'
  const fileInputRef = React.useRef(null);

  const devByImei = useMemo(() => {
    const m = {}; devices.forEach((d) => { if (d.imei) m[String(d.imei).trim()] = d; }); return m;
  }, [devices]);
  const devByName = useMemo(() => {
    const m = {}; devices.forEach((d) => { [d.asset_name, d.unit_number, d.name].filter(Boolean).forEach((n) => { m[String(n).trim().toLowerCase()] = d; }); }); return m;
  }, [devices]);
  const existingByKey = useMemo(() => {
    const m = {}; existingSchedules.forEach((sc) => { if (sc.device_id && sc.task_name && sc.closed !== true) m[`${sc.device_id}::${String(sc.task_name).trim().toLowerCase()}`] = sc; }); return m;
  }, [existingSchedules]);

  // Clone is scoped to the active tab's basis family — a source schedule can only be cloned onto rows
  // of the same family, so km/hrs/days reminder triggers always line up (no cross-basis mismatch).
  const cloneOptions = useMemo(
    () => (existingSchedules || []).filter((s) => s.closed !== true && basisFamily(s.basis) === activeTab),
    [existingSchedules, activeTab]
  );
  const cloneSource = useMemo(() => cloneOptions.find((s) => s.id === cloneSourceId) || null, [cloneOptions, cloneSourceId]);
  // Reset the picked source whenever the tab changes (a source from another family isn't valid here).
  React.useEffect(() => { setCloneSourceId(''); }, [activeTab]);

  const resolveDevice = useCallback((imei, vehicle) => {
    if (imei && devByImei[String(imei).trim()]) return devByImei[String(imei).trim()];
    if (vehicle && devByName[String(vehicle).trim().toLowerCase()]) return devByName[String(vehicle).trim().toLowerCase()];
    return null;
  }, [devByImei, devByName]);

  const validate = useCallback((data) => {
    const errors = [];
    const dev = resolveDevice(data['Device IMEI'], data['Vehicle']);
    if (!dev) errors.push('Vehicle not found — check the Vehicle name (asset / unit / device name) or IMEI');
    const task = String(data['Task Name'] || '').trim();
    if (!task) errors.push('Task Name is required');
    const basis = canonBasis(data['Basis']);
    if (!BASES.includes(basis)) errors.push('Basis must be odometer / engine_hours / calendar / odometer_calendar / engine_hours_calendar');
    const interval = Number(data['Interval (km/hrs)']);
    const intervalDays = Number(data['Interval (days)']);
    if (isMetric(basis) && !(interval > 0)) errors.push('Interval must be greater than 0');
    if (hasCalendar(basis) && !(intervalDays > 0)) errors.push('Interval (days) must be greater than 0');
    if (isMetric(basis) && (data['Last Serviced (km/hrs)'] === '' || data['Last Serviced (km/hrs)'] == null)) errors.push('Last Serviced reading is required');
    const parsedDate = parseDateIn(data[DATE_COL], dateFormat);
    if (hasCalendar(basis) && !parsedDate) errors.push('Last Serviced Date is required (and must be a valid date)');
    if (!hasCalendar(basis) && data[DATE_COL] && !parsedDate) errors.push('Last Serviced Date is not a valid date');
    const key = dev && task ? `${dev.id}::${task.toLowerCase()}` : null;
    const existing = key ? existingByKey[key] : null;
    return { errors, dev, basis, existing, parsedDate };
  }, [resolveDevice, existingByKey, dateFormat]);

  const enriched = useMemo(() => rows.map((r) => {
    const v = validate(r.data);
    const isMatch = !!v.existing;
    const canSelect = v.errors.length === 0 && (!isMatch || duplicateMode === 'update');
    return { ...r, v, isMatch, canSelect };
  }), [rows, validate, duplicateMode]);

  const counts = useMemo(() => {
    let create = 0, update = 0, dupSkipped = 0, invalid = 0;
    enriched.forEach((r) => {
      if (r.v.errors.length) { invalid++; return; }
      if (r.isMatch) {
        if (duplicateMode === 'update' && r.selected) update++;
        else dupSkipped++;
      } else if (r.selected) create++;
    });
    return { create, update, dupSkipped, invalid, total: enriched.length, ready: create + update };
  }, [enriched, duplicateMode]);

  const tabCounts = useMemo(() => {
    const c = { odometer: 0, engine_hours: 0, date: 0 };
    enriched.forEach((r) => { c[tabForBasis(r.v.basis)] += 1; });
    return c;
  }, [enriched]);

  const reset = () => {
    setStep(1); setRows([]); setResults(null); setFileName(''); setDragOver(false);
    setActiveTab('odometer'); setCloneSourceId(''); setDuplicateMode('skip'); setFieldMode('merge');
    setCloneFields({ reminder_stages: true, recurring: false, fixed_schedule: false, interval: false, notes: false });
  };
  const close = () => { reset(); onClose(); };

  const parseFile = (file) => {
    if (!file) return;
    setParsing(true);
    const isXlsx = /\.xlsx$/i.test(file.name);
    const reader = new FileReader();
    reader.onload = (ev) => {
      try {
        let aoa;
        if (isXlsx) {
          const wb = XLSX.read(ev.target.result, { type: 'array', cellText: true, raw: false });
          const ws = wb.Sheets[wb.SheetNames[0]];
          aoa = XLSX.utils.sheet_to_json(ws, { header: 1, raw: false, defval: '' });
        } else {
          const text = new TextDecoder('utf-8').decode(new Uint8Array(ev.target.result)).replace(/^\uFEFF/, '');
          aoa = text.split(/\r?\n/).filter((l) => l.trim() !== '').map((line) => {
            const out = []; let cur = '', q = false;
            for (let i = 0; i < line.length; i++) { const ch = line[i];
              if (q) { if (ch === '"' && line[i + 1] === '"') { cur += '"'; i++; } else if (ch === '"') q = false; else cur += ch; }
              else { if (ch === '"') q = true; else if (ch === ',') { out.push(cur); cur = ''; } else cur += ch; } }
            out.push(cur); return out;
          });
        }
        if (!aoa || aoa.length < 2) { toast.error('File has no data rows'); setParsing(false); return; }
        const header = aoa[0].map((h) => String(h || '').trim());
        const idx = {}; COLUMNS.forEach((c) => { idx[c] = header.findIndex((h) => h.toLowerCase() === c.toLowerCase()); });
        if (idx['Task Name'] === -1 || idx['Basis'] === -1 || (idx['Vehicle'] === -1 && idx['Device IMEI'] === -1)) {
          toast.error('Missing required columns. Use the template.'); setParsing(false); return;
        }
        const parsed = aoa.slice(1).map((r) => {
          const data = {}; COLUMNS.forEach((c) => { data[c] = idx[c] >= 0 ? String(r[idx[c]] ?? '').trim() : ''; });
          // Auto-correct the date to the user's configured display format (parse any format → user format).
          if (data[DATE_COL]) data[DATE_COL] = formatDateOut(parseDateIn(data[DATE_COL], dateFormat), dateFormat) || data[DATE_COL];
          return { data };
        }).filter((r) => Object.values(r.data).some((v) => v !== ''));
        const withSel = parsed.map((r) => {
          const v = validate(r.data);
          return { ...r, cloneOverrides: null, cloned: false, selected: v.errors.length === 0 && !v.existing };
        });
        setRows(withSel);
        setFileName(file.name);
        // Land on the first tab that actually has rows
        const firstTab = ['odometer', 'engine_hours', 'date'].find((t) => withSel.some((r) => tabForBasis(canonBasis(r.data['Basis'])) === t)) || 'odometer';
        setActiveTab(firstTab);
        setStep(2);
      } catch (err) {
        console.error('Schedule import parse failed:', err);
        toast.error('Failed to parse file');
      } finally { setParsing(false); }
    };
    reader.readAsArrayBuffer(file);
  };

  const setCell = (i, col, val) => setRows((prev) => prev.map((r, j) => j === i ? { ...r, data: { ...r.data, [col]: val } } : r));
  const toggleSelected = (i) => setRows((prev) => prev.map((r, j) => j === i ? { ...r, selected: !r.selected } : r));

  const toggleSelectAllInTab = (tab, checked) => setRows((prev) => prev.map((r) => {
    if (tabForBasis(canonBasis(r.data['Basis'])) !== tab) return r;
    const v = validate(r.data);
    const isMatch = !!v.existing;
    const canSel = v.errors.length === 0 && (!isMatch || duplicateMode === 'update');
    return { ...r, selected: !!checked && canSel };
  }));

  const applyClone = () => {
    if (!cloneSource) { toast.error('Choose a schedule to clone from'); return; }
    const keys = Object.keys(cloneFields).filter((k) => cloneFields[k]);
    if (keys.length === 0) { toast.error('Pick at least one field to clone'); return; }
    const srcFamily = basisFamily(cloneSource.basis);
    let touched = 0;
    setRows((prev) => prev.map((r) => {
      const v = validate(r.data);
      // Only selected, valid rows in the SAME basis family as the source (no cross-basis clone).
      if (v.errors.length > 0 || !r.selected || basisFamily(v.basis) !== srcFamily) return r;
      touched++;
      const data = { ...r.data };
      const overrides = { ...(r.cloneOverrides || {}) };
      if (cloneFields.recurring) data['Recurring'] = cloneSource.recurring ? 'yes' : 'no';
      if (cloneFields.fixed_schedule) data['Fixed Schedule'] = cloneSource.fixed_schedule !== false ? 'yes' : 'no';
      if (cloneFields.notes && cloneSource.notes) data['Notes'] = cloneSource.notes;
      if (cloneFields.interval) {
        if (cloneSource.interval_value != null) data['Interval (km/hrs)'] = String(cloneSource.interval_value);
        if (cloneSource.interval_days != null) data['Interval (days)'] = String(cloneSource.interval_days);
      }
      if (cloneFields.reminder_stages) {
        overrides.reminder_stages = (cloneSource.reminder_stages || []).map((st) => ({ ...st, id: undefined, fired_at: null }));
      }
      return { ...r, data, cloneOverrides: overrides, cloned: true };
    }));
    if (touched === 0) toast.error(`No selected ${TAB_META[activeTab].label} rows to clone onto`);
    else toast.success(`Cloned ${keys.length} field(s) from "${schedLabel(cloneSource)}" onto ${touched} selected row(s)`);
  };

  const runImport = async () => {
    setApplying(true); setStep(4);
    const created = [];
    const updated = [];
    const failed = [];
    const selected = enriched.filter((r) => r.selected && r.v.errors.length === 0 && r.canSelect);

    for (const r of selected) {
      const { data, v, isMatch, cloneOverrides } = r;
      const basis = v.basis;
      const isoDate = v.parsedDate;  // canonical YYYY-MM-DD
      const clonedStages = cloneOverrides?.reminder_stages || null;  // reminders come only from Clone
      try {
        if (isMatch && duplicateMode === 'update') {
          const ex = v.existing;
          const payload = {};
          const provided = (key, val) => { if (fieldMode === 'replace' || (val !== '' && val != null)) payload[key] = val; };
          provided('interval_value', isMetric(basis) ? Number(data['Interval (km/hrs)']) : Number(data['Interval (days)'] || 0));
          if (isDual(basis)) provided('interval_days', Number(data['Interval (days)']));
          if (isMetric(basis) && data['Last Serviced (km/hrs)'] !== '') payload.baseline_value = Number(data['Last Serviced (km/hrs)']);
          if (hasCalendar(basis) && isoDate) payload.baseline_date = new Date(`${isoDate}T00:00:00Z`).toISOString();
          if (fieldMode === 'replace' || data['Recurring'] !== '') payload.recurring = yesNo(data['Recurring'], ex.recurring !== false);
          if (fieldMode === 'replace' || data['Fixed Schedule'] !== '') payload.fixed_schedule = yesNo(data['Fixed Schedule'], ex.fixed_schedule !== false);
          if (fieldMode === 'replace' || (data['Notes'] || '') !== '') payload.notes = data['Notes'] || '';
          if (clonedStages) payload.reminder_stages = clonedStages;  // only overwrite reminders if cloned
          const previous = {
            task_name: ex.task_name, interval_value: ex.interval_value, interval_days: ex.interval_days,
            recurring: ex.recurring, fixed_schedule: ex.fixed_schedule, baseline_value: ex.baseline_value,
            baseline_date: ex.baseline_date, reminder_stages: ex.reminder_stages || [], notes: ex.notes,
            next_due_value: ex.next_due_value, next_due_date: ex.next_due_date, schedule_anchor_value: ex.schedule_anchor_value,
          };
          await api.put(`/service/schedules/${ex.id}`, payload);
          updated.push({ schedule_id: ex.id, device_id: v.dev.id, task_name: ex.task_name, previous });
        } else {
          const payload = {
            device_id: v.dev.id, task_name: String(data['Task Name']).trim(), basis,
            interval_value: isMetric(basis) ? Number(data['Interval (km/hrs)']) : Number(data['Interval (days)'] || 0),
            interval_days: isDual(basis) ? Number(data['Interval (days)']) : undefined,
            recurring: yesNo(data['Recurring'], true), fixed_schedule: yesNo(data['Fixed Schedule'], true),
            baseline_value: isMetric(basis) && data['Last Serviced (km/hrs)'] !== '' ? Number(data['Last Serviced (km/hrs)']) : undefined,
            baseline_date: hasCalendar(basis) && isoDate ? new Date(`${isoDate}T00:00:00Z`).toISOString() : undefined,
            notes: data['Notes'] || undefined,
          };
          if (clonedStages && clonedStages.length) payload.reminder_stages = clonedStages;
          const res = await api.post('/service/schedules', payload);
          created.push({ schedule_id: res.data.id, device_id: v.dev.id, task_name: payload.task_name, imei: v.dev.imei });
        }
      } catch (e) {
        failed.push({ task: data['Task Name'], device: data['Vehicle'] || data['Device IMEI'], error: e?.response?.data?.detail || 'Failed' });
      }
    }
    let run = null;
    if (created.length || updated.length) {
      try {
        const res = await api.post('/service/schedules/import-runs', {
          created, updated, summary: { created: created.length, updated: updated.length, failed: failed.length },
        });
        run = res.data;
      } catch { /* non-blocking */ }
    }
    setResults({ created: created.length, updated: updated.length, failed, run });
    setApplying(false);
    if (created.length || updated.length) {
      toast.success(`Imported — ${created.length} created, ${updated.length} updated`);
      onImported && onImported(run);
    }
  };

  const renderGrid = (tab) => {
    const meta = TAB_META[tab];
    const tabRows = enriched.map((r, i) => ({ r, i })).filter(({ r }) => tabForBasis(r.v.basis) === tab);
    const selectableIdx = tabRows.filter(({ r }) => r.canSelect);
    const allSelected = selectableIdx.length > 0 && selectableIdx.every(({ r }) => r.selected);
    if (tabRows.length === 0) {
      return <div className="py-10 text-center text-xs text-muted-foreground">No {meta.label} rows in this file.</div>;
    }
    const showDate = tab !== 'date';   // metric tabs may hold dual-basis rows needing Date columns
    return (
      <div className="border rounded-md overflow-auto max-h-[46vh]" data-testid={`schedule-import-grid-${tab}`}>
        <table className="w-full text-xs">
          <thead className="bg-muted/50 sticky top-0 z-10">
            <tr>
              <th className="p-1.5 text-left w-8">
                <Checkbox checked={allSelected} onCheckedChange={(c) => toggleSelectAllInTab(tab, c)} disabled={step === 3 || selectableIdx.length === 0} data-testid={`schedule-import-selectall-${tab}`} />
              </th>
              <th className="p-1.5 text-left w-8">#</th>
              <th className="p-1.5 text-left font-medium whitespace-nowrap"><span className="inline-flex items-center gap-1">Vehicle<MaintHelpTip topic="imp_vehicle" testId={`import-help-vehicle-${tab}`} /></span></th>
              <th className="p-1.5 text-left font-medium whitespace-nowrap"><span className="inline-flex items-center gap-1">Device IMEI<MaintHelpTip topic="imp_imei" testId={`import-help-imei-${tab}`} /></span></th>
              <th className="p-1.5 text-left font-medium whitespace-nowrap"><span className="inline-flex items-center gap-1">Task Name<MaintHelpTip topic="imp_task" testId={`import-help-task-${tab}`} /></span></th>
              {tab !== 'date' && <th className="p-1.5 text-left font-medium whitespace-nowrap"><span className="inline-flex items-center gap-1">Basis<MaintHelpTip topic="imp_basis" testId={`import-help-basis-${tab}`} /></span></th>}
              {tab !== 'date' && <th className="p-1.5 text-left font-medium whitespace-nowrap"><span className="inline-flex items-center gap-1">Interval ({meta.unit})<MaintHelpTip topic="imp_interval_metric" testId={`import-help-interval-${tab}`} /></span></th>}
              {tab !== 'date' && <th className="p-1.5 text-left font-medium whitespace-nowrap"><span className="inline-flex items-center gap-1">Last Serviced ({meta.unit})<MaintHelpTip topic="imp_last_metric" testId={`import-help-last-${tab}`} /></span></th>}
              {showDate && <th className="p-1.5 text-left font-medium whitespace-nowrap"><span className="inline-flex items-center gap-1">Interval (days)<MaintHelpTip topic="imp_interval_days" testId={`import-help-interval-days-${tab}`} /></span></th>}
              {showDate && <th className="p-1.5 text-left font-medium whitespace-nowrap"><span className="inline-flex items-center gap-1">Last Serviced Date<MaintHelpTip topic="imp_last_date" testId={`import-help-last-date-${tab}`} /></span></th>}
              {tab === 'date' && <th className="p-1.5 text-left font-medium whitespace-nowrap"><span className="inline-flex items-center gap-1">Interval (days)<MaintHelpTip topic="imp_interval_days" testId={`import-help-interval-days-${tab}`} /></span></th>}
              {tab === 'date' && <th className="p-1.5 text-left font-medium whitespace-nowrap"><span className="inline-flex items-center gap-1">Last Serviced Date<MaintHelpTip topic="imp_last_date" testId={`import-help-last-date-${tab}`} /></span></th>}
              <th className="p-1.5 text-left font-medium whitespace-nowrap"><span className="inline-flex items-center gap-1">Recurring<MaintHelpTip topic="imp_recurring" testId={`import-help-recurring-${tab}`} /></span></th>
              <th className="p-1.5 text-left"><span className="inline-flex items-center gap-1">Status<MaintHelpTip topic="imp_status" testId={`import-help-status-${tab}`} /></span></th>
            </tr>
          </thead>
          <tbody>
            {tabRows.map(({ r, i }) => {
              const bad = r.v.errors.length > 0;
              const dual = isDual(r.v.basis);
              const dateEnabled = tab === 'date' || dual;
              return (
                <tr key={i} className={`border-t ${bad ? 'bg-red-50/60 dark:bg-red-950/20' : r.isMatch ? 'bg-amber-50/50 dark:bg-amber-950/20' : ''}`} data-testid={`schedule-import-row-${i}`}>
                  <td className="p-1">
                    <Checkbox checked={!!r.selected} disabled={step === 3 || !r.canSelect} onCheckedChange={() => toggleSelected(i)} data-testid={`schedule-import-select-${i}`} />
                  </td>
                  <td className="p-1 text-muted-foreground">{i + 1}</td>
                  <td className="p-0.5"><Input value={r.data['Vehicle']} onChange={(e) => setCell(i, 'Vehicle', e.target.value)} className="h-7 text-xs min-w-[160px]" disabled={step === 3} /></td>
                  <td className="p-0.5"><Input value={r.data['Device IMEI']} onChange={(e) => setCell(i, 'Device IMEI', e.target.value)} className="h-7 text-xs min-w-[200px] font-mono" disabled={step === 3} /></td>
                  <td className="p-0.5"><Input value={r.data['Task Name']} onChange={(e) => setCell(i, 'Task Name', e.target.value)} className="h-7 text-xs min-w-[190px]" disabled={step === 3} /></td>
                  {tab !== 'date' && (
                    <td className="p-0.5">
                      <Select value={BASES.includes(r.v.basis) ? r.v.basis : meta.bases[0][0]} onValueChange={(val) => setCell(i, 'Basis', val)} disabled={step === 3}>
                        <SelectTrigger className="h-7 text-xs min-w-[150px]" data-testid={`schedule-import-basis-${i}`}><SelectValue /></SelectTrigger>
                        <SelectContent>{meta.bases.map(([v, l]) => <SelectItem key={v} value={v} className="text-xs">{l}</SelectItem>)}</SelectContent>
                      </Select>
                    </td>
                  )}
                  {tab !== 'date' && <td className="p-0.5"><Input value={r.data['Interval (km/hrs)']} onChange={(e) => setCell(i, 'Interval (km/hrs)', e.target.value)} className="h-7 text-xs min-w-[110px]" disabled={step === 3} /></td>}
                  {tab !== 'date' && <td className="p-0.5"><Input value={r.data['Last Serviced (km/hrs)']} onChange={(e) => setCell(i, 'Last Serviced (km/hrs)', e.target.value)} className="h-7 text-xs min-w-[120px]" disabled={step === 3} /></td>}
                  {showDate && <td className="p-0.5"><Input value={r.data['Interval (days)']} onChange={(e) => setCell(i, 'Interval (days)', e.target.value)} className="h-7 text-xs min-w-[100px]" placeholder={dateEnabled ? '' : '—'} disabled={step === 3 || !dateEnabled} /></td>}
                  {showDate && <td className="p-0.5"><Input value={r.data['Last Serviced Date']} onChange={(e) => setCell(i, 'Last Serviced Date', e.target.value)} className="h-7 text-xs min-w-[120px]" placeholder={dateEnabled ? dateFormat : '—'} disabled={step === 3 || !dateEnabled} /></td>}
                  {tab === 'date' && <td className="p-0.5"><Input value={r.data['Interval (days)']} onChange={(e) => setCell(i, 'Interval (days)', e.target.value)} className="h-7 text-xs min-w-[100px]" disabled={step === 3} /></td>}
                  {tab === 'date' && <td className="p-0.5"><Input value={r.data['Last Serviced Date']} onChange={(e) => setCell(i, 'Last Serviced Date', e.target.value)} className="h-7 text-xs min-w-[120px]" placeholder={dateFormat} disabled={step === 3} /></td>}
                  <td className="p-0.5">
                    <Select value={yesNo(r.data['Recurring'], true) ? 'yes' : 'no'} onValueChange={(val) => setCell(i, 'Recurring', val)} disabled={step === 3}>
                      <SelectTrigger className="h-7 text-xs min-w-[80px]" data-testid={`schedule-import-recurring-${i}`}><SelectValue /></SelectTrigger>
                      <SelectContent><SelectItem value="yes" className="text-xs">Yes</SelectItem><SelectItem value="no" className="text-xs">No</SelectItem></SelectContent>
                    </Select>
                  </td>
                  <td className="p-1 min-w-[190px] max-w-[280px]">
                    {bad ? (
                      <span className="text-red-600 flex items-start gap-1 text-[11px] leading-tight" title={r.v.errors.join('\n')} data-testid={`schedule-import-error-${i}`}>
                        <AlertTriangle className="w-3 h-3 mt-0.5 flex-shrink-0" />
                        <span>{r.v.errors[0]}{r.v.errors.length > 1 ? ` (+${r.v.errors.length - 1} more)` : ''}</span>
                      </span>
                    ) : r.isMatch ? (
                      <span className="text-amber-700 flex items-center gap-1 text-[11px]" data-testid={`schedule-import-status-${i}`}>
                        {duplicateMode === 'update' ? (r.selected ? <><Copy className="w-3 h-3" /> will update</> : <>duplicate — tick to update</>) : <>duplicate — skipped</>}
                        {r.cloned && <Badge variant="outline" className="ml-1 text-[9px] px-1 py-0 bg-orange-50 text-orange-700 border-orange-200">Cloned</Badge>}
                      </span>
                    ) : (
                      <span className="text-emerald-600 flex items-center gap-1 text-[11px]" data-testid={`schedule-import-status-${i}`}>
                        <CheckCircle className="w-3 h-3" /> {r.selected ? 'will create' : 'ready'}
                        {r.cloned && <Badge variant="outline" className="ml-1 text-[9px] px-1 py-0 bg-orange-50 text-orange-700 border-orange-200">Cloned</Badge>}
                      </span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) close(); }}>
      <DialogContent className="w-[97vw] max-w-6xl h-[90vh] flex flex-col p-0" data-testid="schedule-import-wizard">
        <DialogHeader className="px-6 pt-6 pb-2">
          <div className="flex items-start justify-between gap-3 pr-8">
            <DialogTitle>Import Service Schedules</DialogTitle>
            <ImportHelpPanel />
          </div>
          <DialogDescription>Upload an .xlsx or .csv, review & fix rows, then import. Imports can be undone for 30 minutes.</DialogDescription>
          <div className="flex items-center gap-4 pt-2">
            <StepDot n={1} step={step} label="Upload" /><ArrowRight className="w-3 h-3 text-muted-foreground" />
            <StepDot n={2} step={step} label="Review" /><ArrowRight className="w-3 h-3 text-muted-foreground" />
            <StepDot n={3} step={step} label="Dry-run" /><ArrowRight className="w-3 h-3 text-muted-foreground" />
            <StepDot n={4} step={step} label="Result" />
          </div>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto px-6 py-2">
          {step === 1 && (
            <div className="space-y-4 py-2" data-testid="schedule-import-wizard-step1">
              <div
                className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${dragOver ? 'border-blue-400 bg-blue-50/60 dark:border-blue-700 dark:bg-blue-950/20' : fileName ? 'border-green-300 dark:border-green-800 bg-green-50/40 dark:bg-green-950/10' : 'border-border'}`}
                onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                onDragLeave={() => setDragOver(false)}
                onDrop={(e) => { e.preventDefault(); setDragOver(false); const f = e.dataTransfer?.files?.[0]; if (!f) return; if (!/\.(csv|xlsx)$/i.test(f.name)) { toast.error('Only .csv and .xlsx files are supported'); return; } parseFile(f); }}
                data-testid="schedule-import-dropzone"
              >
                {parsing ? (
                  <><Loader2 className="w-10 h-10 mx-auto animate-spin text-muted-foreground mb-3" /><p className="text-sm text-muted-foreground">Parsing…</p></>
                ) : (
                  <>
                    <Download className="w-10 h-10 mx-auto text-muted-foreground mb-3" />
                    <p className="text-sm text-muted-foreground mb-4">Drag &amp; drop a schedules CSV or Excel file here, or select one (exported from EnsueLIVETRAK or matching the same column names).</p>
                  </>
                )}
                <input ref={fileInputRef} type="file" accept=".csv,.xlsx,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" className="hidden" data-testid="schedule-import-file-input" onChange={(e) => { parseFile(e.target.files?.[0]); e.target.value = ''; }} />
                <Button variant="outline" onClick={() => fileInputRef.current?.click()} data-testid="schedule-import-select-file-btn">
                  <FileSpreadsheet className="w-4 h-4 mr-2 text-[#2563eb]" /> Select CSV / Excel file
                </Button>
                <div className="mt-4 flex flex-wrap items-center justify-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
                  <span className="font-semibold text-foreground">Templates:</span>
                  <Button variant="link" size="sm" className="text-xs h-auto py-0 text-muted-foreground hover:text-foreground" onClick={() => downloadScheduleTemplateXlsx(dateFormat)} data-testid="schedule-import-template-xlsx"><Download className="w-3 h-3 mr-1" /> Excel-safe (.xlsx)</Button>
                  <span>·</span>
                  <Button variant="link" size="sm" className="text-xs h-auto py-0 text-muted-foreground hover:text-foreground" onClick={() => downloadScheduleTemplateCsv(dateFormat)} data-testid="schedule-import-template-csv"><Download className="w-3 h-3 mr-1" /> CSV (.csv)</Button>
                </div>
              </div>
              <p className="text-[11px] text-muted-foreground text-center">Match vehicles by <b>Vehicle</b> name (asset / unit / device name) — IMEI is optional. Rows are grouped into Odometer / Engine Hours / Date tabs on the next step. Dates are auto-detected from your format; reminder stages are added on the next step via <b>Clone</b>.</p>
            </div>
          )}

          {(step === 2 || step === 3) && (
            <div className="space-y-3">
              {/* Mode + clone controls (Review only) */}
              {step === 2 && (
                <div className="space-y-3">
                  <div className="rounded-lg border p-3 bg-background/60" data-testid="schedule-import-mode">
                    <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
                      <div>
                        <p className="text-xs font-semibold mb-1">Existing schedules (same vehicle + task)</p>
                        <RadioGroup value={duplicateMode} onValueChange={setDuplicateMode} className="flex gap-4">
                          <label className="flex items-center gap-1.5 text-xs cursor-pointer"><RadioGroupItem value="skip" data-testid="schedule-import-mode-skip" /> Create only (skip matches)</label>
                          <label className="flex items-center gap-1.5 text-xs cursor-pointer"><RadioGroupItem value="update" data-testid="schedule-import-mode-update" /> Update existing</label>
                        </RadioGroup>
                      </div>
                      {duplicateMode === 'update' && (
                        <div>
                          <p className="text-xs font-semibold mb-1">How updates apply</p>
                          <RadioGroup value={fieldMode} onValueChange={setFieldMode} className="flex gap-4">
                            <label className="flex items-center gap-1.5 text-xs cursor-pointer"><RadioGroupItem value="merge" data-testid="schedule-import-field-merge" /> Merge (only filled cells)</label>
                            <label className="flex items-center gap-1.5 text-xs cursor-pointer"><RadioGroupItem value="replace" data-testid="schedule-import-field-replace" /> Replace (overwrite all)</label>
                          </RadioGroup>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="rounded-lg border p-3 bg-background/60" data-testid="schedule-import-clone">
                    <div className="flex items-center gap-2 mb-2">
                      <Copy className="w-4 h-4 text-[#2563eb]" />
                      <p className="text-sm font-semibold">Clone from an existing schedule</p>
                      <Badge variant="outline" className="text-[10px] px-1.5 py-0">{TAB_META[activeTab].label} tab</Badge>
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                      <ScheduleClonePicker value={cloneSourceId} onChange={setCloneSourceId} options={cloneOptions} testid="schedule-import-clone-picker" />
                      <Button size="sm" variant="outline" onClick={applyClone} disabled={!cloneSource}
                        className={cloneSource ? 'bg-orange-500 hover:bg-orange-600 text-white border-orange-500 hover:text-white' : ''}
                        data-testid="schedule-import-clone-apply">Clone to selected</Button>
                      <span className="text-[11px] text-muted-foreground">Lists only <b>{TAB_META[activeTab].label}</b> schedules and applies the ticked fields to selected rows <b>in this tab</b> — so reminder units always match.</span>
                    </div>
                    <div className="flex flex-wrap gap-3 mt-2">
                      {CLONE_FIELDS.map(([key, label]) => (
                        <label key={key} className={`flex items-center gap-1.5 text-xs ${cloneSourceId ? 'cursor-pointer' : 'cursor-not-allowed opacity-50'}`} title={cloneSourceId ? undefined : 'Select a schedule to clone from first'}>
                          <Checkbox checked={!!cloneFields[key]} disabled={!cloneSourceId} onCheckedChange={(v) => setCloneFields((prev) => ({ ...prev, [key]: !!v }))} data-testid={`schedule-import-clone-field-${key}`} />
                          {label}
                        </label>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              <div className="flex items-center gap-2 text-xs flex-wrap">
                <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200">{counts.create} to create</Badge>
                {duplicateMode === 'update' && <Badge variant="outline" className="bg-orange-50 text-orange-700 border-orange-200">{counts.update} to update</Badge>}
                <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200">{counts.dupSkipped} duplicates skipped</Badge>
                <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200">{counts.invalid} with errors</Badge>
                <span className="text-muted-foreground ml-auto">{counts.total} rows</span>
              </div>

              <Tabs value={activeTab} onValueChange={setActiveTab}>
                <TabsList data-testid="schedule-import-tabs">
                  {['odometer', 'engine_hours', 'date'].map((t) => (
                    <TabsTrigger key={t} value={t} data-testid={`schedule-import-tab-${t}`}>{TAB_META[t].label} ({tabCounts[t]})</TabsTrigger>
                  ))}
                </TabsList>
                {['odometer', 'engine_hours', 'date'].map((t) => (
                  <TabsContent key={t} value={t} className="mt-2">{renderGrid(t)}</TabsContent>
                ))}
              </Tabs>

              {step === 3 && (
                <div className="rounded-md border bg-muted/30 p-3 text-sm" data-testid="schedule-import-dryrun">
                  <p className="font-medium">Dry-run summary</p>
                  <p className="text-muted-foreground text-xs mt-1"><b className="text-emerald-600">{counts.create}</b> will be created · <b className="text-orange-600">{counts.update}</b> updated · <b className="text-amber-600">{counts.dupSkipped}</b> duplicates skipped · <b className="text-red-600">{counts.invalid}</b> blocked by errors. No changes have been made yet.</p>
                </div>
              )}
            </div>
          )}

          {step === 4 && (
            <div className="flex flex-col items-center justify-center h-full gap-3 text-center">
              {applying ? (
                <><Loader2 className="w-8 h-8 animate-spin text-[#2563eb]" /><p className="text-sm">Importing…</p></>
              ) : results ? (
                <>
                  <CheckCircle className="w-10 h-10 text-emerald-500" />
                  <p className="text-lg font-semibold">{results.created} created · {results.updated} updated</p>
                  {results.failed.length > 0 && (
                    <div className="text-xs text-red-600 max-h-40 overflow-y-auto w-full max-w-md text-left border rounded-md p-2">
                      {results.failed.map((f, i) => <p key={i}>• {f.device} — {f.task}: {typeof f.error === 'string' ? f.error : 'failed'}</p>)}
                    </div>
                  )}
                  <p className="text-xs text-muted-foreground">You can undo this import for the next 30 minutes from the Schedules tab.</p>
                </>
              ) : null}
            </div>
          )}
        </div>

        <DialogFooter className="px-6 py-4 border-t">
          {step === 1 && <Button variant="outline" onClick={close} data-testid="schedule-import-cancel">Cancel</Button>}
          {step === 2 && (
            <>
              <Button variant="outline" onClick={() => setStep(1)}><ArrowLeft className="w-4 h-4 mr-1" /> Back</Button>
              <Button onClick={() => setStep(3)} disabled={counts.ready === 0} data-testid="schedule-import-to-dryrun">Dry-run — {counts.ready} ready <ArrowRight className="w-4 h-4 ml-1" /></Button>
            </>
          )}
          {step === 3 && (
            <>
              <Button variant="outline" onClick={() => setStep(2)}><ArrowLeft className="w-4 h-4 mr-1" /> Back to edit</Button>
              <Button onClick={runImport} disabled={counts.ready === 0} data-testid="schedule-import-apply"><CheckCircle className="w-4 h-4 mr-1" /> Import {counts.ready} Schedule{counts.ready === 1 ? '' : 's'}</Button>
            </>
          )}
          {step === 4 && !applying && <Button onClick={close} data-testid="schedule-import-done">Done</Button>}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
